import { useCallback, useRef } from 'react';
import axios from 'axios';
import { testMediaConnectionBitrate, MediaConnectionBitrateTest } from '@twilio/rtc-diagnostics';
import { ACTIONTYPE } from '../AppStateProvider';
import { BITRATE_TEST_DURATION } from '../../../constants';

// Helper function to calculate connection stability score
function calculateStabilityScore(bitrateHistory: number[]): number {
  if (bitrateHistory.length < 2) return 100;

  const mean = bitrateHistory.reduce((a, b) => a + b, 0) / bitrateHistory.length;
  const variance = bitrateHistory.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / bitrateHistory.length;
  const standardDeviation = Math.sqrt(variance);
  const coefficientOfVariation = (standardDeviation / mean) * 100;

  // Lower coefficient of variation = higher stability
  return Math.max(0, 100 - coefficientOfVariation);
}

export default function useBitrateTest(dispatch: React.Dispatch<ACTIONTYPE>, testDuration: number = BITRATE_TEST_DURATION) {
  const bitrateTestRef = useRef<MediaConnectionBitrateTest>();
  const metricsIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const testTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const bitrateHistoryRef = useRef<number[]>([]);
  const cancelledRef = useRef<boolean>(false);

  const startBitrateTest = useCallback(() => {
    //Don't start a new bitrate test if one is already running:
    if (bitrateTestRef.current) {
      return;
    }

    dispatch({ type: 'bitrate-test-started' });
    cancelledRef.current = false;
    bitrateHistoryRef.current = []; // Reset history early, before token fetch — ensures clean slate even if fetch fails

    return axios('app/turn-credentials')
      .then((response) => {
        const bitrateTest = testMediaConnectionBitrate({ iceServers: response.data.iceServers });

        bitrateTestRef.current = bitrateTest;

        bitrateTest.on(MediaConnectionBitrateTest.Events.Bitrate, (bitrate) => {
          if (cancelledRef.current) return; // Ignore events after cancellation
          dispatch({ type: 'set-bitrate', bitrate });
          dispatch({ type: 'set-current-bitrate', bitrate });

          // Store bitrate history for quality analysis
          bitrateHistoryRef.current.push(bitrate);

          // Calculate quality metrics every 10 seconds for 10-minute test
          if (bitrateHistoryRef.current.length % 10 === 0) {
            const recentBitrates = bitrateHistoryRef.current.slice(-30); // Last 30 seconds of data
            const avgBitrate = recentBitrates.reduce((a, b) => a + b, 0) / recentBitrates.length;
            const bitrateVariation = Math.max(...recentBitrates) - Math.min(...recentBitrates);

            // Calculate additional long-term metrics
            const allTimeBitrates = bitrateHistoryRef.current;
            const overallAvg = allTimeBitrates.reduce((a, b) => a + b, 0) / allTimeBitrates.length;
            const consistencyScore = calculateStabilityScore(allTimeBitrates);

            dispatch({
              type: 'update-quality-metrics',
              metrics: {
                avgBitrate,
                bitrateVariation,
                stabilityScore: consistencyScore,
                overallAverage: overallAvg,
                testDuration: allTimeBitrates.length, // seconds elapsed
              },
            });
          }
        });

        bitrateTest.on(MediaConnectionBitrateTest.Events.Error, (error) => {
          if (cancelledRef.current) return; // Ignore events after cancellation
          dispatch({ type: 'set-bitrate-test-error', error });
          dispatch({ type: 'bitrate-test-finished' });
        });

        bitrateTest.on(MediaConnectionBitrateTest.Events.End, (report) => {
          // Clear any intervals
          if (metricsIntervalRef.current) {
            clearInterval(metricsIntervalRef.current);
          }
          bitrateTestRef.current = undefined;

          // If cancelled, the partial report was already dispatched — don't overwrite it
          if (cancelledRef.current) return;

          // Add final quality analysis to report
          const finalReport = {
            ...report,
            qualityMetrics: {
              totalSamples: bitrateHistoryRef.current.length,
              avgBitrate: bitrateHistoryRef.current.reduce((a, b) => a + b, 0) / bitrateHistoryRef.current.length,
              minBitrate: Math.min(...bitrateHistoryRef.current),
              maxBitrate: Math.max(...bitrateHistoryRef.current),
              bitrateStability: calculateStabilityScore(bitrateHistoryRef.current),
            },
          };

          dispatch({ type: 'set-bitrate-test-report', report: finalReport });
          dispatch({ type: 'bitrate-test-finished' });
        });

        // Add progress tracking for extended test
        const startTime = Date.now();
        progressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = (elapsed / testDuration) * 100;
          dispatch({ type: 'set-bitrate-test-progress', progress: Math.min(progress, 100) });

          if (elapsed >= testDuration) {
            clearInterval(progressIntervalRef.current!);
          }
        }, 1000); // Update progress every second

        testTimeoutRef.current = setTimeout(() => {
          clearInterval(progressIntervalRef.current!);
          bitrateTest.stop();
        }, testDuration);
      })
      .catch((error) => {
        console.error('Error running the bitrate test', error);
        dispatch({ type: 'set-bitrate-test-error', error });
        dispatch({ type: 'bitrate-test-finished' });
      });
  }, [dispatch, testDuration]);

  const cancelBitrateTest = useCallback(() => {
    // Prevent any post-cancellation events from overwriting the partial report
    cancelledRef.current = true;

    // Clean up all intervals and timeouts
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (testTimeoutRef.current) {
      clearTimeout(testTimeoutRef.current);
    }
    if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
    }

    // Create final report with collected data before stopping
    if (bitrateHistoryRef.current.length > 0) {
      const avgBitrate = bitrateHistoryRef.current.reduce((a, b) => a + b, 0) / bitrateHistoryRef.current.length;

      const partialReport = {
        // Required MediaConnectionBitrateTest.Report properties
        averageBitrate: avgBitrate,
        values: bitrateHistoryRef.current,
        errors: [], // No errors since it was manually cancelled
        testName: 'bitrate-test',
        testTiming: {
          start: Date.now() - bitrateHistoryRef.current.length * 1000, // Approximate start time
          end: Date.now(),
          duration: bitrateHistoryRef.current.length * 1000, // milliseconds
        },
        iceCandidateStats: [], // Empty for cancelled test

        // Additional quality metrics for cancelled test
        qualityMetrics: {
          totalSamples: bitrateHistoryRef.current.length,
          avgBitrate: avgBitrate,
          minBitrate: Math.min(...bitrateHistoryRef.current),
          maxBitrate: Math.max(...bitrateHistoryRef.current),
          bitrateStability: calculateStabilityScore(bitrateHistoryRef.current),
          testDuration: bitrateHistoryRef.current.length,
          cancelled: true,
          completionPercentage: Math.round((bitrateHistoryRef.current.length / 600) * 100), // Based on 10-minute target
        },
      } as any; // Cast to any to allow extra qualityMetrics property

      // Dispatch the partial report
      dispatch({ type: 'set-bitrate-test-report', report: partialReport });
    }

    // Stop the bitrate test if running
    if (bitrateTestRef.current) {
      bitrateTestRef.current.stop();
      bitrateTestRef.current = undefined;
    }

    // Mark test as cancelled and finished
    dispatch({ type: 'bitrate-test-cancelled' });
    dispatch({ type: 'bitrate-test-finished' });
  }, [dispatch]);

  return { startBitrateTest, cancelBitrateTest } as const;
}
